<template>
  <div class="ta-auth-card ta-signup-form">
    <div class="text-center">
      <h3 class="font-weight-bold mt-3">{{ $t("Identity Verification") }}</h3>
    </div>

    <h4 v-if="customer.term" class="text-xl">
      {{$t('Identity Status')}}:
      <span v-if="customer.term.identity_verification === 'unverified'" class="text-danger">{{ $t('Unverified') }}</span>
      <span v-else-if="customer.term.identity_verification === 'verified'" class="text-success">{{ $t('Verified') }}</span>
      <span v-else class="text-warning">{{ $t('Pending') }}</span>
    </h4>

    <FormAlert :success-message="successMessage" :error-message="errorMessage"/>

    <ValidationObserver ref="signupValidationObserver" v-slot="{handleSubmit }">
      <b-overlay :show="loading || uploadingFiles" rounded="sm">
        <form ref="profileForm" @submit.prevent="validateForm(handleSubmit)" autocomplete="off">
          <input autocomplete="false" name="hidden" type="text" style="display:none;" />
          <personal-identification-form v-if="customer.term" :disabled="customer.term.identity_verification === 'pending'" />
          <LoadingButton :loading="loading || uploadingFiles" class="mr-3 ta-btn-submit">
            {{ uploadingFiles ? $t("Uploading files...") : $t("Save") }}
          </LoadingButton>
        </form>
      </b-overlay>
    </ValidationObserver>
  </div>
</template>

<script>
import {mapActions, mapGetters, mapMutations, mapState} from "vuex";
import LoadingButton from "../../components/core/LoadingButton.vue";
import InputWidget from "../../components/core/InputWidget.vue";
import FormAlert from "../../components/core/FormAlert.vue";
import ProfileBody from "@/components/configuration/profile/ProfileBody.vue";
import PersonalIdentificationForm from "@/views/IdentityVerification/PersonalIdentificationForm.vue";

export default {
  name: "IdentityVerification",
  data() {
    return {
      loading: true,
      uploadingFiles: false,
      errorMessage: null,
      successMessage: null,
    };
  },
  computed: {
    ...mapGetters(["currentLocale", "availableLanguages", 'profilePasswordUpdateFilled']),
    ...mapState(["customer"]),
    ...mapState({
      model: state => state.identityVerification.model,
      errors: state => state.identityVerification.errors
    }),
  },
  methods: {
    ...mapActions(["customerIdentificationUpdate", "socketLoginCallback"]),
    ...mapMutations(['setIdentityVerificationModel', 'resetIdentityVerificationModel', 'identityVerificationFieldErrors', 'identityVerificationFieldOnInput']),

    scrollToTop() {
      const invalidInput = this.$refs.profileForm.querySelector('.is-invalid');
      if (invalidInput) {
        invalidInput.scrollIntoView();
      }
    },

    async onSubmit() {
      this.loading = true;
      
      try {
        // Extract file metadata for presigned URL generation
        const payload = this.preparePayloadForServer();
        
        // Send metadata to server to get presigned URLs
        const response = await this.customerIdentificationUpdate({'payload': payload});
        
        // Upload actual files to presigned URLs
        this.uploadingFiles = true;
        try {
          await this.uploadFilesToPresignedUrls(response.presigned_urls);
          this.uploadingFiles = false;
        } catch (uploadError) {
          this.uploadingFiles = false;
          throw uploadError;
        }
        
        // Update the store with the response data to reflect the current state
        this.updateModelFromResponse(response);
        
        this.loading = false;
        this.successMessage = this.$t('Identity Verification details updated successfully');
        this.scrollToTop();
      } catch (errors) {
        this.loading = false;
        this.uploadingFiles = false;
        this.successMessage = null;

        this.identityVerificationFieldErrors({
          ...errors,
        })

        setTimeout(() => {
          this.scrollToTop();
        }, 500)
      }
    },

    preparePayloadForServer() {
      const payload = { ...this.model };
      
      // Convert file objects to metadata objects (name and size only)
      const fileFields = ['government_issued_id', 'proof_of_address', 'employment_information', 'company_id_documents'];
      
      fileFields.forEach(field => {
        if (payload[field] && Array.isArray(payload[field])) {
          payload[field] = payload[field].map(file => ({
            name: file.name,
            size: file.size
          }));
        }
      });
      
      // Include deleted_files array in the payload
      if (payload.deleted_files && Array.isArray(payload.deleted_files)) {
        payload.deleted_files = [...payload.deleted_files];
      } else {
        payload.deleted_files = [];
      }
      
      // Include dynamic_fields in the payload
      if (payload.dynamic_fields && typeof payload.dynamic_fields === 'object') {
        payload.dynamic_fields = {...payload.dynamic_fields};
      } else {
        payload.dynamic_fields = {};
      }
      
      return payload;
    },

    async uploadFilesToPresignedUrls(presignedUrls) {
      console.log('=== UPLOAD DEBUG START ===');
      console.log('presignedUrls received:', presignedUrls);
      console.log('model file fields:', {
        government_issued_id: this.model.government_issued_id,
        proof_of_address: this.model.proof_of_address,
        employment_information: this.model.employment_information,
        company_id_documents: this.model.company_id_documents
      });
      
      const fileFields = ['government_issued_id', 'proof_of_address', 'employment_information', 'company_id_documents'];
      
      for (const field of fileFields) {
        console.log(`\n--- Processing field: ${field} ---`);
        console.log(`1111111111111111111111111`);
        console.log(`model[${field}]:`, this.model[field]);
        console.log(`2222222222222222222222222`);
        console.log(field, presignedUrls);
        console.log(`3333333333333333333333333`);
        console.log(`Array.isArray(this.model[${field}]):`, Array.isArray(this.model[field]));
        console.log(`presignedUrls[${field}] exists:`, presignedUrls[field] !== undefined);
        console.log(`presignedUrls[${field}] length:`, presignedUrls[field]?.length);
        
        if (this.model[field] && Array.isArray(this.model[field]) && presignedUrls[field] && Array.isArray(presignedUrls[field])) {
          const files = this.model[field];
          const matchedUrls = presignedUrls[field];
          
          console.log(`✅ Uploading ${files.length} files for ${field}:`, files.map(f => f.name));
          console.log(`Matched URLs:`, matchedUrls);
          
          // Create a map of file names to URLs for easier matching
          const urlMap = {};
          matchedUrls.forEach(matchedUrl => {
            if (matchedUrl.name && matchedUrl.url) {
              urlMap[matchedUrl.name] = matchedUrl.url;
            }
          });
          
          for (let i = 0; i < files.length; i++) {
            const file = files[i];
            const presignedUrl = urlMap[file.name];
            
            console.log(`\nProcessing file ${i + 1}/${files.length}: ${file.name}`);
            console.log(`URL for file ${file.name}:`, presignedUrl);
            
            if (presignedUrl) {
              try {
                console.log(`🚀 Starting upload of ${file.name} to: ${presignedUrl}`);
                await this.uploadFileToPresignedUrl(file, presignedUrl);
                console.log(`✅ Successfully uploaded: ${file.name}`);
              } catch (error) {
                console.error(`❌ Failed to upload ${file.name}:`, error);
                throw new Error(`Failed to upload ${file.name}: ${error.message}`);
              }
            } else {
              console.warn(`⚠️ No presigned URL available for file: ${file.name}`);
            }
          }
        } else {
          console.log(`❌ Skipping field ${field} - conditions not met`);
          console.log(`  - model[${field}] exists:`, !!this.model[field]);
          console.log(`  - is array:`, Array.isArray(this.model[field]));
          console.log(`  - presignedUrls[${field}] exists:`, !!presignedUrls[field]);
          console.log(`  - presignedUrls[${field}] is array:`, Array.isArray(presignedUrls[field]));
        }
      }
      
      console.log('=== UPLOAD DEBUG END ===');
    },

    async uploadFileToPresignedUrl(file, presignedUrl) {
      try {
        console.log(`Starting upload of ${file.name} (${file.size} bytes) to: ${presignedUrl}`);
        
        const response = await fetch(presignedUrl, {
          method: 'PUT',
          headers: {
            'Content-Type': file.type,
          },
          body: file,
        });
        
        if (response.ok) {
          console.log(`File uploaded successfully: ${file.name}`);
          return;
        } else {
          const errorText = await response.text();
          console.error(`Upload failed with status: ${response.status}`, errorText);
          throw new Error(`Upload failed with status: ${response.status} - ${errorText}`);
        }
      } catch (error) {
        console.error(`Upload failed for ${file.name}:`, error);
        throw new Error(`Upload failed for ${file.name}: ${error.message}`);
      }
    },

    updateModelFromResponse(response) {
      // Update the model with the response data to reflect the current server state
      if (response && response.artisio_response) {
        const serverData = response.artisio_response;
        
        // Update customer identification files if present in response
        if (serverData.customer_identification_files) {
          this.identityVerificationFieldOnInput({
            field: 'identification_files',
            value: serverData.customer_identification_files
          });
        }
        
        // Clear the deleted_files array since the deletion has been processed
        this.identityVerificationFieldOnInput({
          field: 'deleted_files',
          value: []
        });
        
        // Clear file input fields after successful update
        // This prevents uploaded files from appearing twice in the UI
        const fileFields = ['government_issued_id', 'proof_of_address', 'employment_information', 'company_id_documents'];
        fileFields.forEach(field => {
          this.identityVerificationFieldOnInput({
            field: field,
            value: null
          });
        });
        
        // Update other fields if present
        if (serverData.document_type) {
          this.identityVerificationFieldOnInput({
            field: 'document_type',
            value: serverData.document_type
          });
        }
        
        if (serverData.document_number) {
          this.identityVerificationFieldOnInput({
            field: 'document_number',
            value: serverData.document_number
          });
        }
        
        if (serverData.document_issue_date) {
          this.identityVerificationFieldOnInput({
            field: 'document_issue_date',
            value: serverData.document_issue_date
          });
        }
        
        if (serverData.document_expiry_date) {
          this.identityVerificationFieldOnInput({
            field: 'document_expiry_date',
            value: serverData.document_expiry_date
          });
        }
        
        if (serverData.dynamic_fields) {
          this.identityVerificationFieldOnInput({
            field: 'dynamic_fields',
            value: serverData.dynamic_fields
          });
        }
      }
    },

    async validateForm(handleSubmit) {
      // before validate
      await handleSubmit(this.onSubmit)
      // after validate
      this.scrollToTop()
    },
  },
  components: {PersonalIdentificationForm, ProfileBody, FormAlert, InputWidget, LoadingButton},
  async beforeMount() {
    this.socketLoginCallback()
      .then((customer) => {
        this.setIdentityVerificationModel(customer.identification)
        this.$nextTick(() => {
          this.$refs.signupValidationObserver.reset()
        })
        this.loading = false;
      });
  },
  destroyed() {
    this.resetIdentityVerificationModel()
  }
};
</script>

<style lang="scss" scoped>
@import "../../bootstrap/import";

#artisioTimedAuctions {
  .ta-signup-form {
    width: 800px;
    padding: 2rem;
  }

  .ta-signup-form {
    button {
      width: 140px;
    }
  }
}
</style>
